import { Box, Button, Card, CardHeader, IconButton, Paper, Typography } from '@mui/material';
import App from 'AppInterface/App';
import DialogPage from 'components/pages/DialogPage';
import PassLockDialog from './PassLockDialog';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FullScreenPlayerTitle from 'components/player/FullScreenPlayerTitle';
import { BottomDrawerOptions } from 'components/shell/BottomDrawerOptions';
import { TimeSelectorControl } from 'components/TimeSelectorControl';
import { Icons } from 'config/icons';
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { getTimerDurationByDate, getTimerLogsByDate } from 'store/data/timer/selectors';
import { toggleBottomDrawer, updateTimerRunState } from 'store/temp/actions';
import { updateDhyanDaySchedule, updateDhyanGoal, updateDhyanOnHome, updateDhyanPassLock, updateDhyanStartDate } from 'store/ux/actions';
import { ApplicationState, TimerRunningState, TimerState, TimerType } from 'types';
import { TimerAnalytics } from './TimerAnalytics';
import { TimerLogsList } from './TimerLogs';
import { TimerPresetWidget } from './TimerPresetWidget';
import { TimerProgress } from './TimerProgress';
import { TimerRunningControl } from './TimerRunningControl';
import { MobileDatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { User } from 'firebase/auth';
import { withRouter } from 'components/shell/WithRouter';

const classes = {
	root: (props) => ({
		display: props.enabled ? 'flex' : 'none',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		height: '100%',
	}),
	title: {
		flex: '0 0 auto',
	},
	progressWrap: {
		backgroundColor: '#f2f4ef',
		display: 'flex',
		flexDirection: 'column',
		padding: 0.5,
		height: '100%',
	},
	paper: { px: 2, py: 1, m: 0.5, borderRadius: 2 },
};

interface Props {
	enabled: boolean;
	timerState?: TimerState;
	durationsByDate: { [date: string]: number };
	showDhyanOnHome: boolean;
	dhyanDaySchedule: number;
	dhyanGoal: number;
	dhyanStartDate: number;
	minDate: string;
	user?: User;
	dhyanPassLock: string;
	history: any;
	toggleFullScreen: (e: React.MouseEvent) => void;
	stopTimer: (e: React.MouseEvent) => void;
	updateTimerRunState: (currentState: TimerRunningState) => void;
	toggleDrawer: (content?: any) => void;
	updateDhyanOnHome: (enabled: boolean) => void;
	updateDhyanDaySchedule: (schedule: number) => void;
	updateDhyanGoal: (goal: number) => void;
	updateDhyanStartDate: (startDate: number) => void;
	updatePassLock: (passLock: string) => void;
}
const Component = (props: Props) => {
	let rootRef = useRef<HTMLElement>();
	let {
		timerState,
		durationsByDate,
		showDhyanOnHome,
		dhyanDaySchedule,
		dhyanGoal,
		dhyanStartDate,
		minDate,
		enabled,
		user,
		history,
		toggleFullScreen,
		stopTimer,
		updateTimerRunState,
		toggleDrawer,
		updateDhyanOnHome,
		updateDhyanDaySchedule,
		updateDhyanGoal,
		updateDhyanStartDate,
	} = props;

	let [isLarge, setLarge] = useState(true);
	let [timerLogsVisible, setTimerLogsVisible] = useState(false);
	let [isVerified, setIsVerified] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			if (rootRef.current) {
				let ch = rootRef.current.clientHeight;
				setLarge(ch > 825);
			}
		}, 100);
	}, [enabled]);

	useEffect(() => {
		if (user && props.dhyanPassLock) {
			setPassLockOpen(true);
			setPassLockMode('verify');
			setIsVerified(false);
		} else {
			setIsVerified(true);
		}
	}, [user, props.dhyanPassLock]);

	let [tempCompleted, setTempCompleted] = useState(-1);
	let [tempDate, setTempDate] = useState('');
	let [isCustomMode, setIsCustomMode] = useState(false);
	let [passLockOpen, setPassLockOpen] = useState(false);
	let [passLockMode, setPassLockMode] = useState<'verify' | 'set'>('verify');

	if (timerLogsVisible) {
		return (
			<DialogPage
				title={'Dhyan Logs'}
				dark
				goBackOnClose={false}
				onClose={() => {
					setTimerLogsVisible(false);
				}}
			>
				<TimerLogsList />
			</DialogPage>
		);
	}

	const optionsObj = () => {
		return {
			options: [
				{
					title: showDhyanOnHome ? 'Hide from Home' : 'Show on Home',
					icon: showDhyanOnHome ? Icons.BookmarkRemove : Icons.BookmarkAdd,
					onClick: () => {
						updateDhyanOnHome(!showDhyanOnHome);
						toggleDrawer();
					},
				},
				{
					title: 'Increase Daily Goal',
					icon: Icons.Flag,
					onClick: () => {
						toggleDrawer();
						toggleDrawer(
							BottomDrawerOptions({
								element: (
									<Box sx={{ width: '100%' }}>
										<Typography sx={{ m: 1, textAlign: 'center' }} variant="subtitle2" color="CaptionText">
											Increase Your Goal By
										</Typography>
										<TimeSelectorControl
											maxHours={6}
											value={(dhyanGoal - 120) * 60}
											onAccept={(secs) => {
												updateDhyanGoal(120 + Math.floor(secs / 60));

												toggleDrawer();
											}}
										/>
									</Box>
								),
								options: [],
								actionHandler: () => {
									toggleDrawer();
								},
							})
						);
					},
				},
				{
					title: 'Change Start Date',
					icon: Icons.Calendar,
					onClick: () => {
						toggleDrawer();
						toggleDrawer(
							BottomDrawerOptions({
								options: [
									{
										element: (
											<Box sx={{ display: 'flex', flexDirection: 'row' }}>
												<MobileDatePicker
													minDate={moment(minDate)}
													maxDate={moment()}
													value={dhyanStartDate > 0 ? moment.unix(dhyanStartDate) : undefined}
													label="Select Start Date"
													format="DD/MM/YYYY"
													onAccept={(date: Moment | null) => {
														if (date) {
															updateDhyanStartDate(date.unix());
															toggleDrawer();
														}
													}}
												/>
												{dhyanStartDate > 0 && (
													<IconButton
														onClick={(e) => {
															e.stopPropagation();
															updateDhyanStartDate(0);
															toggleDrawer();
														}}
													>
														<Icons.ReplayCircleFilledIcon />
													</IconButton>
												)}
											</Box>
										),
									},
								],
								actionHandler: () => {
									toggleDrawer();
								},
							})
						);
					},
				},
				{
					title: 'Change Day Schedule',
					icon: Icons.Schedule,
					onClick: () => {
						toggleDrawer();
						toggleDrawer(
							BottomDrawerOptions({
								element: (
									<Box sx={{ width: '100%' }}>
										<Typography sx={{ m: 1, textAlign: 'center' }} variant="subtitle2" color="CaptionText">
											Shift Day Start Time By
										</Typography>
										<TimeSelectorControl
											maxHours={8}
											onlyHour={true}
											ampm={true}
											value={dhyanDaySchedule}
											onAccept={(secs) => {
												updateDhyanDaySchedule(secs);

												toggleDrawer();
											}}
										/>
									</Box>
								),
								options: [],
								actionHandler: () => {
									toggleDrawer();
								},
							})
						);
					},
				},
				{
					title: !props.dhyanPassLock ? 'Set Pass Lock' : 'Remove Pass Lock',
					icon: !props.dhyanPassLock ? <LockIcon /> : <LockOpenIcon />,
					onClick: () => {
						if (props.dhyanPassLock) {
							props.updatePassLock('');
						} else {
							setPassLockOpen(true);
							setPassLockMode('set');
						}
						toggleDrawer();
					},
				},
				{
					title: 'Update Data Manually',
					icon: Icons.Edit,
					onClick: () => {
						setIsCustomMode(false);
						setTempDate('');
						setTempCompleted(-1);

						setTimerLogsVisible(true);
						toggleDrawer();
					},
				},
			],
			actionHandler: () => {
				toggleDrawer();
			},
		};
	};

	const onMoreIconClick = (e: React.MouseEvent) => {
		e.stopPropagation();

		toggleDrawer(BottomDrawerOptions(optionsObj()));
	};

	let navigate = history.navigate;

	return (
		<Box ref={rootRef} sx={classes.root(props)}>
			<PassLockDialog
				open={passLockOpen}
				mode={passLockMode}
				onClose={() => {
					setPassLockOpen(false);
				}}
				onSuccess={() => {
					setPassLockOpen(false);
					if (passLockMode === 'verify') {
						setIsVerified(true);
					}
				}}
			/>
			<Box sx={classes.title}>
				<FullScreenPlayerTitle
					title={'Dhyan'}
					onMinimize={(e) => {
						if (timerState?.currentState === TimerRunningState.Running) {
							updateTimerRunState(TimerRunningState.Paused);
							App.pauseCountDownTimer();
						}
						toggleFullScreen(e);
					}}
					showMin={timerState?.currentState === TimerRunningState.Running || timerState?.currentState === TimerRunningState.Paused}
					onClose={(e) => {
						if (timerState?.currentState === TimerRunningState.Running || timerState?.currentState === TimerRunningState.Paused) {
							updateTimerRunState(TimerRunningState.Stopped);
							App.cancelCountDownTimer();
						}

						stopTimer(e);
					}}
				/>
			</Box>
			{isVerified && (
				<>
					{timerState && timerState.currentState !== TimerRunningState.Stopped && timerState.type === TimerType.Dhyan ? (
						<Box sx={classes.progressWrap} onClick={() => {}}>
							<Box sx={{ flexGrow: 1 }}></Box>
							<Paper sx={classes.paper}>
								<TimerRunningControl sx={{}} />
							</Paper>
							<Box sx={{ flexGrow: 1 }}></Box>
						</Box>
					) : (
						<Box sx={classes.progressWrap} onClick={() => {}}>
							{!user ? (
								<Card sx={{ m: 0.5 }}>
									<CardHeader
										subheader="Signin to persist progress"
										action={
											<Button
												style={{ marginTop: 8 }}
												size="small"
												onClick={() => {
													navigate('/account?screen=signin', { state: { isModal: true } });
												}}
											>
												Sign In
											</Button>
										}
									/>
								</Card>
							) : null}
							<Card sx={{ ...classes.paper, p: 0, flex: '1 0 auto', position: 'relative', minHeight: 240 }}>
								<CardHeader
									action={
										<IconButton
											onClick={(e) => {
												onMoreIconClick(e);
											}}
										>
											{Icons.MoreVert}
										</IconButton>
									}
								></CardHeader>
								<TimerProgress
									customDate={tempDate}
									customDuration={tempCompleted}
									size={'large'}
									sx={{
										textAlign: 'center',
										position: 'absolute',
										top: 'calc(50% - 110px)',
										left: 'calc(50% - 99px)',
									}}
								/>
							</Card>

							<Paper sx={classes.paper}>
								<TimerAnalytics
									onDateClick={(date: string) => {
										setTempDate(date);
										let tempDuration = date.length > 0 ? durationsByDate[date] : -1;
										setTempCompleted(tempDuration);
										setIsCustomMode(date.length > 0);
									}}
									isCustomMode={isCustomMode}
									sx={{ flexGrow: 1 }}
								/>
							</Paper>

							<Paper sx={classes.paper}>
								<TimerPresetWidget sx={isLarge ? { flexWrap: 'wrap', height: 265, justifyContent: 'center' } : {}} />
							</Paper>
						</Box>
					)}
				</>
			)}

			{/* <Paper
				sx={{ ...classes.controls, ...classes.paper, bottom: hiddenControls ? '-220px' : 0, boxShadow: '0 -1px 5px lightgrey', height: 100 }}
			></Paper> */}
		</Box>
	);
};

function mapStateToProps(state: ApplicationState, props) {
	let timerState = state.tempState.timerState;
	let durationsByDate = getTimerDurationByDate(state);

	let timerLogsByDate = getTimerLogsByDate(state);
	let minDate = Object.keys(timerLogsByDate).sort((a, b) => (a > b ? 1 : -1))[0];

	return {
		timerState,
		durationsByDate,
		showDhyanOnHome: state.uxState.showDhyanOnHome ?? true,
		dhyanDaySchedule: state.uxState.dhyanDaySchedule,
		dhyanGoal: state.uxState.dhyanGoal,
		dhyanStartDate: state.uxState.dhyanStartDate,
		minDate,
		user: state.userState.userStore.user,
		dhyanPassLock: state.uxState.dhyanPassLock || '',
	};
}

function mapDispatchToProps(dispatch) {
	return {
		updateTimerRunState: (currentState: TimerRunningState) => {
			dispatch(updateTimerRunState(currentState));
		},
		toggleDrawer: (content?: any) => {
			dispatch(toggleBottomDrawer(content));
		},
		updateDhyanOnHome: (enabled: boolean) => {
			dispatch(updateDhyanOnHome(enabled));
		},
		updateDhyanDaySchedule: (schedule: number) => {
			dispatch(updateDhyanDaySchedule(schedule));
		},
		updateDhyanGoal: (goal: number) => {
			dispatch(updateDhyanGoal(goal));
		},
		updateDhyanStartDate: (startDate: number) => {
			dispatch(updateDhyanStartDate(startDate));
		},
		updatePassLock: (passLock: string) => {
			dispatch(updateDhyanPassLock(passLock));
		},
	};
}

export const FullScreenTimerControl = connect(mapStateToProps, mapDispatchToProps)(withRouter(Component));
